/**
 * Saved spreadsheet views for the batch/cohort applicant table.
 *
 * Admins build a column set + column ORDER once ("Finance review", "Visa
 * queue") and reuse it, instead of re-ticking the column picker every visit.
 * Views live in localStorage — browser-scoped, like the ticket drafts — so no
 * Firestore rule or schema change is needed.
 *
 * Dependency-free and pure so it can be unit-tested and reused by any table.
 */

export interface BatchTableView {
  id: string;
  name: string;
  /** Column ids in display order — the order IS the admin's Excel-style layout. */
  columns: string[];
  createdAt: string;
  updatedAt: string;
}

export const BATCH_VIEW_STORAGE_KEY = 'isats:batch-table-views';
export const BATCH_ACTIVE_VIEW_KEY = 'isats:batch-table-active-view';

/** Default column set + order for the cohort spreadsheet. */
export const DEFAULT_TABLE_COLUMNS: string[] = ['applicantId', 'fullName', 'status', 'program', 'partner'];

function canStore(): boolean {
  return typeof window !== 'undefined' && !!window.localStorage;
}

export function makeViewId(): string {
  return `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function isView(value: any): value is BatchTableView {
  return !!value
    && typeof value.id === 'string'
    && typeof value.name === 'string'
    && Array.isArray(value.columns)
    && value.columns.every((c: any) => typeof c === 'string');
}

export function loadBatchViews(): BatchTableView[] {
  if (!canStore()) return [];
  try {
    const raw = window.localStorage.getItem(BATCH_VIEW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isView) : [];
  } catch {
    return [];
  }
}

export function persistBatchViews(views: BatchTableView[]): void {
  if (!canStore()) return;
  try {
    window.localStorage.setItem(BATCH_VIEW_STORAGE_KEY, JSON.stringify(views));
  } catch { /* quota / private mode — views are best-effort */ }
}

export function loadActiveViewId(): string | null {
  if (!canStore()) return null;
  try {
    return window.localStorage.getItem(BATCH_ACTIVE_VIEW_KEY) || null;
  } catch {
    return null;
  }
}

export function persistActiveViewId(id: string | null): void {
  if (!canStore()) return;
  try {
    if (id) window.localStorage.setItem(BATCH_ACTIVE_VIEW_KEY, id);
    else window.localStorage.removeItem(BATCH_ACTIVE_VIEW_KEY);
  } catch { /* best-effort */ }
}

/**
 * Move a column to another column's position (Excel-style drag). The dragged
 * column lands exactly where it was dropped, shifting the rest along.
 */
export function moveColumnOrder(columns: string[], fromId: string, toId: string): string[] {
  if (fromId === toId) return columns;
  const fromIdx = columns.indexOf(fromId);
  const toIdx = columns.indexOf(toId);
  if (fromIdx === -1 || toIdx === -1) return columns;
  const next = [...columns];
  next.splice(fromIdx, 1);
  // Re-resolve the drop target after removal so the insert index is correct.
  const insertAt = next.indexOf(toId);
  if (insertAt === -1) return columns;
  next.splice(insertAt, 0, fromId);
  return next;
}

/** Keep only ids that still exist, then append any newly-available columns. */
export function reconcileColumns(stored: string[], available: string[]): string[] {
  const known = stored.filter(id => available.includes(id));
  const missing = available.filter(id => !known.includes(id));
  return [...known, ...missing];
}
